import { useCallback, useRef } from "react";
import { PostgrestFilterBuilder } from "@supabase/postgrest-js";

type QueryKey = string;
type QueryResult<T> = {
  data: T | null;
  error: any;
  count?: number | null;
};

export function useQueryCache() {
  const cache = useRef<
    Map<QueryKey, { result: QueryResult<any>; timestamp: number }>
  >(new Map());
  const CACHE_TTL = 60 * 1000; // 1 minute

  const executeQuery = useCallback(
    async <T>(
      query: PostgrestFilterBuilder<any, any, any, any, any>,
      queryKey: string,
    ): Promise<QueryResult<T>> => {
      const now = Date.now();
      const cached = cache.current.get(queryKey);

      // Check if we have a valid cached result
      if (cached && now - cached.timestamp < CACHE_TTL) {
        return cached.result;
      }

      // Execute the query
      const result = await query;

      // Cache the result
      cache.current.set(queryKey, {
        result,
        timestamp: now,
      });

      return result;
    },
    [],
  );

  const invalidateCache = useCallback((queryKey?: string) => {
    if (queryKey) {
      cache.current.delete(queryKey);
    } else {
      cache.current.clear();
    }
  }, []);

  return {
    executeQuery,
    invalidateCache,
  };
}
